import { useSyncExternalStore } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { startImpersonation as requestImpersonation } from '../services/impersonationService';

const RETURN_TOKEN_KEY = 'impersonation:adminReturnToken';

const listeners = new Set<() => void>();

function notify(): void {
    listeners.forEach(listener => listener());
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
    return sessionStorage.getItem(RETURN_TOKEN_KEY) !== null;
}

/** True while the current session is an admin impersonating another user. */
export function useIsImpersonating(): boolean {
    return useSyncExternalStore(subscribe, getSnapshot);
}

/** Swaps the current (admin) session for the target user's session via a server-minted custom token. */
export async function loginAsUser(targetUserId: string): Promise<void> {
    const { targetToken, adminReturnToken } = await requestImpersonation(targetUserId);
    // Only mark impersonation as active once the session swap has actually succeeded —
    await signInWithCustomToken(auth, targetToken);
    sessionStorage.setItem(RETURN_TOKEN_KEY, adminReturnToken);
    notify();
}

/**
 * Swaps back to the admin session using the stashed return token.
 * Falls back to a full logout if the token is missing/expired, since custom tokens expire after 1 hour.
 */
export async function returnToAdmin(logout: () => Promise<void>): Promise<boolean> {
    const returnToken = sessionStorage.getItem(RETURN_TOKEN_KEY);
    sessionStorage.removeItem(RETURN_TOKEN_KEY);
    notify();

    if (!returnToken) {
        await logout();
        return false;
    }

    try {
        await signInWithCustomToken(auth, returnToken);
        return true;
    } catch {
        await logout();
        return false;
    }
}

/** Clears any leftover impersonation state, e.g. on a normal logout. */
export function clearImpersonationState(): void {
    sessionStorage.removeItem(RETURN_TOKEN_KEY);
    notify();
}
